import type { StageStatus } from '@/types';

/**
 * Everything the setup surfaces need to describe an in-flight automation run,
 * derived from the raw stage list the backend reports over socket and polling.
 *
 * Pure on purpose: the top bar, the gated-page panel and the tests all read
 * from this one derivation, so they can never disagree about which step the
 * user is on or how far along it is.
 */
export interface AutomationProgressView {
  /** Stages that count toward progress. Skipped stages are dropped entirely. */
  activeStages: StageStatus[];
  /** The stage to describe to the user: errored first, then running, then next up. */
  currentStage: StageStatus | null;
  /** 1-based position of `currentStage` among the active stages, 0 when unknown. */
  currentStepNumber: number;
  totalSteps: number;
  /** 0-100 across the whole pipeline, weighting the in-flight stage's own progress. */
  overallPercent: number;
  /** The backend's live sub-step message, e.g. "Generating post 3 of 12...". */
  detailMessage: string | null;
  erroredStage: StageStatus | null;
  isComplete: boolean;
  /** True while no stage has reported anything yet — there is nothing truthful to show. */
  isPending: boolean;
}

const EMPTY_VIEW: AutomationProgressView = {
  activeStages: [],
  currentStage: null,
  currentStepNumber: 0,
  totalSteps: 0,
  overallPercent: 0,
  detailMessage: null,
  erroredStage: null,
  isComplete: false,
  isPending: true,
};

function clampPercent(value: number | undefined): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0;
  return Math.min(100, Math.max(0, value));
}

export function deriveAutomationProgress(stages: StageStatus[]): AutomationProgressView {
  // A skipped stage never runs — counting it would both inflate the step total
  // and strand the bar below 100% forever.
  const activeStages = stages.filter(s => s.status !== 'skipped');
  const totalSteps = activeStages.length;
  if (totalSteps === 0) return EMPTY_VIEW;

  const erroredStage = activeStages.find(s => s.status === 'error') ?? null;
  const processing = activeStages.find(s => s.status === 'processing') ?? null;
  const completedCount = activeStages.filter(s => s.status === 'completed').length;
  const isComplete = activeStages.every(s => s.status === 'completed');

  // Nothing has reported in yet — most often the couple of seconds between the
  // app seeding the store and the first poll landing. Claiming "step 1 of 5"
  // here would be a guess, and a wrong one whenever the run is already at
  // stage 4, so the view stays deliberately blank instead.
  const isPending = !erroredStage && !processing && completedCount === 0 && !isComplete;

  // A failure outranks anything still marked processing: the user needs to see
  // what broke, not a stage that will never finish behind it.
  const currentStage =
    isComplete || isPending
      ? null
      : (erroredStage ?? processing ?? activeStages.find(s => s.status === 'pending') ?? null);

  const currentStepNumber = currentStage
    ? activeStages.indexOf(currentStage) + 1
    : isComplete
      ? totalSteps
      : 0;

  const fraction = processing ? clampPercent(processing.progress) / 100 : 0;
  const raw = ((completedCount + fraction) / totalSteps) * 100;
  // Cap below 100 while work remains — a bar that reads "100%" and keeps
  // spinning is the exact frustration this screen exists to remove.
  const overallPercent = isComplete ? 100 : Math.min(99, Math.round(raw));

  const message = (erroredStage ?? processing)?.message;

  return {
    activeStages,
    currentStage,
    currentStepNumber,
    totalSteps,
    overallPercent,
    detailMessage: message ? message : null,
    erroredStage,
    isComplete,
    isPending,
  };
}

/**
 * A fingerprint of "the backend told us something new", used to notice a
 * stalled run. Covers the message as well as the progress number because the
 * final stage holds one progress value while counting posts up in the message.
 *
 * Null whenever nothing is running — an idle or failed pipeline must never be
 * reported as stalled.
 */
export function stageActivityKey(view: AutomationProgressView): string | null {
  const running = view.activeStages.find(s => s.status === 'processing');
  if (!running) return null;
  return `${running.stage}:${running.progress ?? 0}:${running.message ?? ''}`;
}

/** Human-readable elapsed time, e.g. "45s" or "2m 14s". */
export function formatElapsed(ms: number): string {
  // Backend `startedAt` versus browser clock can skew negative; show 0 rather
  // than a nonsense duration.
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}
